import { Component, Input, OnInit, ViewEncapsulation, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataScopesEnum } from '../../shared/enums/index';
import { AppGlobals } from '../../shared/globals/appGlobals';
import { fadeInOut } from '../../shared/helpers/animations';
import { ICustomer, IIngestedDevicesChart, IPermission } from '../../shared/models/index';
import { AuthorizationService, StorageManagerService } from '../../shared/services/index';
import { SubmittedDevicesChartComponent } from '../submitted-devices-chart/submitted-devices-chart.component';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})

export class DashboardComponent implements OnInit, AfterViewInit {

  subscription: Subscription;
  permissions: any;
  ingestedDevicesData: IIngestedDevicesChart[] = [];
  submittedDevicesData: SubmittedDevicesChartComponent[] = [];

  @Input() customer: ICustomer;
  @Input() user: any;

  localTime: any;
  userTimeZone: any;

  dataScope: any;

  customersWithTemplates: any[] = [];
  localCustomers: any[] = [];
  
  // Add property to cache documents for each channel
  channelDocuments: { [key: string]: any[] } = {};

  constructor (
    private authorizationService: AuthorizationService, 
    private storageManagerService: StorageManagerService, 
    private appGlobals: AppGlobals,
    private cdr: ChangeDetectorRef
  ) { }

  getTimeZone() {

    this.localTime = new Date().getTimezoneOffset();
    this.userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
    // or

    let offset = new Date().getTimezoneOffset(), o = Math.abs(offset);
    return (offset < 0 ? "+" : "-") + ("00" + Math.floor(o / 60)).slice(-2) + ":" + ("00" + (o % 60)).slice(-2);
  }

  ngOnInit() {
    this.dataScope = DataScopesEnum;
    this.permissions = this.appGlobals.permissions;

    // Prepare channel documents once during initialization
    this.prepareChannelDocuments();

    // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();
  }

  ngAfterViewInit(): void {
    // Ensure the view is stable after animation
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 500); // Wait for animation to complete
  }

  // Prepare documents for all channels during initialization
  prepareChannelDocuments(): void {
    const allDocuments = {
      'EUDAMED': [
        {
          filename: 'EUDAMED Supporting Documents (Column Templates).zip',
          displayName: 'EUDAMED Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for EUDAMED data.',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'EUDAMED Supporting Documents (Row Templates).zip',
          displayName: 'EUDAMED Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for EUDAMED data.',
          icon: 'fal fa-file-archive'
        }
      ],
      'Swissdamed': [
        {
          filename: 'Swissdamed Supporting Documents (Column Templates).zip',
          displayName: 'Swissdamed Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for Swissdamed data.',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'Swissdamed Supporting Documents (Row Templates).zip',
          displayName: 'Swissdamed Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for Swissdamed data.',
          icon: 'fal fa-file-archive'
        }
      ],
      'China-UDI': [
        {
          filename: 'China Supporting Documents (Column Templates).zip',
          displayName: 'China Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for China-UDI',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'China Supporting Documents (Row Templates).zip',
          displayName: 'China Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for China-UDI',
          icon: 'fal fa-file-archive'
        }
      ],
      'SouthKorea-UDI': [
        {
          filename: 'South Korea Supporting Documents (Column Templates).zip',
          displayName: 'South Korea Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for SouthKorea-UDI',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'South Korea Supporting Documents (Row Templates).zip',
          displayName: 'South Korea Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for SouthKorea-UDI',
          icon: 'fal fa-file-archive'
        }
      ],
      'US-UDI': [
        {
          filename: 'US GUDID Supporting Documents (Column Templates).zip',
          displayName: 'US GUDID Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for UDI-US GUDID',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'US GUDID Supporting Documents (Row Templates).zip',
          displayName: 'US GUDID Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for UDI-US GUDID',
          icon: 'fal fa-file-archive'
        }
      ],
      'Brazil-UDI': [
        {
          filename: 'UDI-Brazil GUDID Supporting Documents (Column Expansion Templates).zip',
          displayName: 'UDI-Brazil GUDID Supporting Documents (Column Expansion Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for UDI-Brazil GUDID',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'UDI-Brazil GUDID Supporting Documents (Row Expansion Templates).zip',
          displayName: 'UDI-Brazil GUDID Supporting Documents (Row Expansion Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for UDI-Brazil GUDID',
          icon: 'fal fa-file-archive'
        }
      ],
      'Australia-UDI': [
        {
          filename: 'Australia Supporting Documents (Column Templates).zip',
          displayName: 'Australia Supporting Documents (Column Templates)',
          description: 'Zip file containing the data collection forms supporting column-based expansion for Australia-UDI',
          icon: 'fal fa-file-archive'
        },
        {
          filename: 'Australia Supporting Documents (Row Templates).zip',
          displayName: 'Australia Supporting Documents (Row Templates)',
          description: 'Zip file containing the data collection forms supporting row-based expansion for Australia-UDI',
          icon: 'fal fa-file-archive'
        }
      ]
    };

    // Cache documents using the exact application variant names
    Object.keys(allDocuments).forEach(key => {
      this.channelDocuments[key] = allDocuments[key];
      this.channelDocuments[key.toLowerCase()] = allDocuments[key];
    });
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById('firstDiv');

    if (main) {
      main.focus();
    }
  }

  showDocs(permission) {

    return this.authorizationService.hasPermissionPerClient(permission);
  }

  showPageContents(): boolean {

    let retVal = false;

    const permissions: IPermission[] = this.storageManagerService.read('accessPermissions');

    retVal = !!permissions;

    return retVal;
  }

  // Filtering out the customers that don't have templates so the headers don't print
  filterCustomersWithNoTemplates() {
    // Pulling in the customer data from the storage manager
    const currentCustomerStr: string = this.storageManagerService.read('currentCustomer');
    if (currentCustomerStr) {
      this.customer = typeof currentCustomerStr === 'object' ? currentCustomerStr : JSON.parse(currentCustomerStr);
    }
    
    if (!this.customer || !this.customer.applicationVariants) {
      return [];
    }
    
    this.localCustomers = this.customer.applicationVariants;
    const noTemplatesAvailable = ["saudiarabia-udi"];

    this.customersWithTemplates = (this.localCustomers as any).filter(x => 
      !(noTemplatesAvailable as any).includes(x['applicationVariantName'].toLowerCase())
    );

    return this.customersWithTemplates;
  }

  // Method to handle document downloads with proper timing
  downloadDocument(filename: string, event?: Event): void {
    //console.log('Download clicked for:', filename); // Debug log
    //alert(`Download clicked for: ${filename}`); // Temporary debug alert
    
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Ensure DOM is stable before creating download link
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = `../../../assets/documents/${filename}`;
      link.download = filename;
      link.target = '_blank';
      
      // Append to body temporarily
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 0);
  }

  // Method to get documents for a specific channel (optimized version)
  getDocumentsForChannel(applicationVariantName: string): any[] {
    if (!applicationVariantName) {
      return [];
    }
    
    // Try exact match first
    if (this.channelDocuments[applicationVariantName]) {
      return this.channelDocuments[applicationVariantName];
    }
    
    // Try lowercase match
    if (this.channelDocuments[applicationVariantName.toLowerCase()]) {
      return this.channelDocuments[applicationVariantName.toLowerCase()];
    }
    
    // Fallback for any other cases
    return [];
  }

  // TrackBy function for better performance with *ngFor
  trackByDoc(index: number, doc: any): string {
    return doc?.filename || index.toString();
  }

  // TrackBy function for channels
  trackByChannel(index: number, channel: any): string {
    return channel?.applicationVariantName || index.toString();
  }
}
